import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

export type AppTheme = 'dark-industrial' | 'light';
export type OperationalColorKey =
  | 'severityCritical'
  | 'severityHigh'
  | 'severityMedium'
  | 'severityLow'
  | 'statusOnline'
  | 'statusOffline'
  | 'statusWarning'
  | 'statusInfo'
  | 'validationPending'
  | 'validationConfirmed'
  | 'validationRejected'
  | 'validationInconclusive';

const STORAGE_KEY = 'aql-vision-theme';
const OPERATIONAL_COLOR_STORAGE_KEY = 'aql-vision-operational-colors';
const DEFAULT_THEME: AppTheme = 'dark-industrial';

export const DEFAULT_OPERATIONAL_COLORS: Record<AppTheme, Record<OperationalColorKey, string>> = {
  'dark-industrial': {
    severityCritical: '#f43f5e',
    severityHigh: '#fb7185',
    severityMedium: '#f59e0b',
    severityLow: '#38bdf8',
    statusOnline: '#22c55e',
    statusOffline: '#ef4444',
    statusWarning: '#f59e0b',
    statusInfo: '#38bdf8',
    validationPending: '#38bdf8',
    validationConfirmed: '#22c55e',
    validationRejected: '#f43f5e',
    validationInconclusive: '#a78bfa'
  },
  light: {
    severityCritical: '#e11d48',
    severityHigh: '#f97316',
    severityMedium: '#f59e0b',
    severityLow: '#0ea5e9',
    statusOnline: '#10b981',
    statusOffline: '#dc2626',
    statusWarning: '#d97706',
    statusInfo: '#0284c7',
    validationPending: '#0ea5e9',
    validationConfirmed: '#059669',
    validationRejected: '#e11d48',
    validationInconclusive: '#7c3aed'
  }
};

const colorPattern = /^#([0-9a-f]{6})$/i;

const cssVariableByOperationalColor: Record<OperationalColorKey, string> = {
  severityCritical: '--aql-severity-critical',
  severityHigh: '--aql-severity-high',
  severityMedium: '--aql-severity-medium',
  severityLow: '--aql-severity-low',
  statusOnline: '--aql-status-online',
  statusOffline: '--aql-status-offline',
  statusWarning: '--aql-status-warning',
  statusInfo: '--aql-status-info',
  validationPending: '--aql-validation-pending',
  validationConfirmed: '--aql-validation-confirmed',
  validationRejected: '--aql-validation-rejected',
  validationInconclusive: '--aql-validation-inconclusive'
};

const isAppTheme = (value: string | null): value is AppTheme =>
  value === 'dark-industrial' || value === 'light';

export const resolveInitialTheme = (): AppTheme => {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  const savedTheme = window.localStorage.getItem(STORAGE_KEY);
  if (savedTheme === 'glass') {
    window.localStorage.setItem(STORAGE_KEY, DEFAULT_THEME);
    return DEFAULT_THEME;
  }
  return isAppTheme(savedTheme) ? savedTheme : DEFAULT_THEME;
};

export const applyThemeToDocument = (theme: AppTheme) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark-industrial');
  root.setAttribute('data-theme', theme);
  root.style.colorScheme = theme === 'dark-industrial' ? 'dark' : 'light';
  root.removeAttribute('data-glass-period');
  applyOperationalColorsToDocument(theme);
};

const normalizeOperationalColors = (value: unknown): Record<AppTheme, Record<OperationalColorKey, string>> => {
  const source = value && typeof value === 'object' ? value as Record<string, unknown> : {};

  return (Object.keys(DEFAULT_OPERATIONAL_COLORS) as AppTheme[]).reduce((acc, theme) => {
    const themeSource = source[theme] && typeof source[theme] === 'object'
      ? source[theme] as Record<string, unknown>
      : {};
    acc[theme] = (Object.keys(DEFAULT_OPERATIONAL_COLORS[theme]) as OperationalColorKey[]).reduce((colors, key) => {
      const candidate = String(themeSource[key] ?? '').trim();
      colors[key] = colorPattern.test(candidate) ? candidate : DEFAULT_OPERATIONAL_COLORS[theme][key];
      return colors;
    }, {} as Record<OperationalColorKey, string>);
    return acc;
  }, {} as Record<AppTheme, Record<OperationalColorKey, string>>);
};

export const readOperationalColors = (): Record<AppTheme, Record<OperationalColorKey, string>> => {
  if (typeof window === 'undefined') return DEFAULT_OPERATIONAL_COLORS;
  try {
    return normalizeOperationalColors(JSON.parse(window.localStorage.getItem(OPERATIONAL_COLOR_STORAGE_KEY) ?? '{}'));
  } catch {
    return DEFAULT_OPERATIONAL_COLORS;
  }
};

export const saveOperationalColors = (colors: Record<AppTheme, Record<OperationalColorKey, string>>) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(OPERATIONAL_COLOR_STORAGE_KEY, JSON.stringify(normalizeOperationalColors(colors)));
};

export const applyOperationalColorsToDocument = (theme: AppTheme, colorSource = readOperationalColors()) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const colors = colorSource[theme] ?? DEFAULT_OPERATIONAL_COLORS[theme];

  (Object.keys(cssVariableByOperationalColor) as OperationalColorKey[]).forEach((key) => {
    root.style.setProperty(cssVariableByOperationalColor[key], colors[key]);
  });

  root.style.setProperty('--status-critical', colors.severityCritical);
  root.style.setProperty('--status-warning', colors.statusWarning);
  root.style.setProperty('--status-normal', colors.statusOnline);
  root.style.setProperty('--status-info', colors.statusInfo);
};

interface ThemeContextValue {
  theme: AppTheme;
  setTheme: (theme: AppTheme) => void;
  isDark: boolean;
  operationalColors: Record<AppTheme, Record<OperationalColorKey, string>>;
  setOperationalColor: (theme: AppTheme, key: OperationalColorKey, color: string) => void;
  resetOperationalColors: (theme?: AppTheme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<AppTheme>(() => resolveInitialTheme());
  const [operationalColors, setOperationalColors] = useState(() => readOperationalColors());
  const transitionTimerRef = useRef<number | null>(null);

  useEffect(() => {
    applyThemeToDocument(theme);
    applyOperationalColorsToDocument(theme, operationalColors);
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme, operationalColors]);

  useEffect(() => {
    return () => {
      if (transitionTimerRef.current !== null) {
        window.clearTimeout(transitionTimerRef.current);
      }
    };
  }, []);

  const setThemeWithTransition = (nextTheme: AppTheme) => {
    if (nextTheme === theme) return;
    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      root.classList.add('theme-transitioning');
      if (transitionTimerRef.current !== null) {
        window.clearTimeout(transitionTimerRef.current);
      }
      transitionTimerRef.current = window.setTimeout(() => {
        root.classList.remove('theme-transitioning');
      }, 220);
    }
    setThemeState(nextTheme);
  };

  const setOperationalColor = (targetTheme: AppTheme, key: OperationalColorKey, color: string) => {
    if (!colorPattern.test(color)) return;
    setOperationalColors((current) => {
      const next = normalizeOperationalColors({
        ...current,
        [targetTheme]: {
          ...current[targetTheme],
          [key]: color
        }
      });
      saveOperationalColors(next);
      applyOperationalColorsToDocument(theme, next);
      return next;
    });
  };

  const resetOperationalColors = (targetTheme?: AppTheme) => {
    setOperationalColors((current) => {
      const next = normalizeOperationalColors(
        targetTheme
          ? { ...current, [targetTheme]: DEFAULT_OPERATIONAL_COLORS[targetTheme] }
          : DEFAULT_OPERATIONAL_COLORS
      );
      saveOperationalColors(next);
      applyOperationalColorsToDocument(theme, next);
      return next;
    });
  };

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme: (nextTheme) => setThemeWithTransition(nextTheme),
      isDark: theme === 'dark-industrial',
      operationalColors,
      setOperationalColor,
      resetOperationalColors
    }),
    [theme, operationalColors]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useAppTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useAppTheme must be used inside ThemeProvider');
  return context;
};
